import { useNavigate } from 'react-router-dom'
import './DroneCard.css'

export interface DroneCardData {
  id: string
  drone_id: string
  tail_number?: string | null
  status: string
  battery_level?: number | null
  last_lat?: string | null
  last_lng?: string | null
  last_heartbeat_at?: string | null
  last_telemetry_at?: string | null
  altitude?: number | null
  speed?: number | null
  roll?: number | null
  pitch?: number | null
  heading?: number | null
  gps_fix_type?: number | null
  flight_mode?: string | null
  drone_model?: string | null
}

/* ───────── Attitude Direction Indicator (ADI) ───────── */
function ADI({ roll = 0, pitch = 0 }: { roll?: number; pitch?: number }) {
  const S = 62          // total SVG size
  const CX = S / 2
  const CY = S / 2
  const R = S / 2 - 2  // inner radius (leaves 2px for border)

  const rollRad = (-roll * Math.PI) / 180
  const cosR = Math.cos(rollRad)
  const sinR = Math.sin(rollRad)

  // Pitch: +pitch → nose up → horizon shifts DOWN in display
  // pitchPx: 1.2px per degree, clamped so horizon doesn't leave circle
  const pitchPx = Math.max(-R * 0.85, Math.min(R * 0.85, (pitch / 90) * R * 1.2))

  // Horizon center offset along gravity direction (sinR, cosR)
  const hcx = CX + pitchPx * sinR
  const hcy = CY + pitchPx * cosR

  // Horizon line endpoints (long enough to span circle)
  const len = R * 1.6
  const h1x = hcx - len * cosR
  const h1y = hcy + len * sinR
  const h2x = hcx + len * cosR
  const h2y = hcy - len * sinR

  // Ground polygon: horizon + large offset in gravity direction
  const gx = sinR * R * 2.5
  const gy = cosR * R * 2.5
  const groundPts = `${h1x},${h1y} ${h2x},${h2y} ${h2x + gx},${h2y + gy} ${h1x + gx},${h1y + gy}`

  // Roll index tick (top of circle, rotates with roll)
  const tickLen = 7
  const t1x = CX + (R - tickLen) * Math.sin(rollRad)
  const t1y = CY - (R - tickLen) * Math.cos(rollRad)
  const t2x = CX + R * Math.sin(rollRad)
  const t2y = CY - R * Math.cos(rollRad)

  // Pitch ladder lines (±5°, ±10°, ±15°) drawn in horizon-rotated space
  const ladderLines = [-15, -10, -5, 5, 10, 15].map((deg) => {
    const offsetPx = (deg / 90) * R * 1.2
    const halfW = deg % 10 === 0 ? 10 : 6
    // Center of this pitch line (perpendicular to horizon direction)
    const lcx = hcx - offsetPx * sinR
    const lcy = hcy - offsetPx * cosR
    return {
      deg,
      x1: lcx - halfW * cosR,
      y1: lcy + halfW * sinR,
      x2: lcx + halfW * cosR,
      y2: lcy - halfW * sinR,
    }
  })

  const clipId = `adi-clip-${Math.random().toString(36).slice(2, 7)}`

  return (
    <svg width={S} height={S} className="adi-svg" aria-label={`ADI roll:${roll} pitch:${pitch}`}>
      <defs>
        <clipPath id={clipId}>
          <circle cx={CX} cy={CY} r={R} />
        </clipPath>
      </defs>

      {/* ── Clipped world ── */}
      <g clipPath={`url(#${clipId})`}>
        {/* Sky */}
        <rect x={0} y={0} width={S} height={S} fill="#1a4a8a" />
        {/* Sky gradient */}
        <rect x={0} y={0} width={S} height={S} fill="url(#skyGrad)" opacity={0.4} />
        {/* Ground */}
        <polygon points={groundPts} fill="#5a3210" />
        {/* Ground texture line */}
        <polygon points={groundPts} fill="#7a4a1a" opacity={0.4} />
        {/* Horizon line */}
        <line x1={h1x} y1={h1y} x2={h2x} y2={h2y} stroke="white" strokeWidth={1.5} />
        {/* Pitch ladder */}
        {ladderLines.map((l) => (
          <line
            key={l.deg}
            x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke="rgba(255,255,255,0.7)"
            strokeWidth={1}
          />
        ))}
        {/* Center cross-hairs (subtle) */}
        <line x1={CX - R * 0.3} y1={CY} x2={CX - 4} y2={CY} stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
        <line x1={CX + 4} y1={CY} x2={CX + R * 0.3} y2={CY} stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
      </g>

      {/* ── Outer border ── */}
      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#555" strokeWidth={1.5} />

      {/* ── Roll index (rotates with roll, outside clip) ── */}
      <line x1={t1x} y1={t1y} x2={t2x} y2={t2y} stroke="#f5c518" strokeWidth={2} />

      {/* ── Aircraft symbol (FIXED, always centred) ── */}
      {/* Left wing */}
      <line x1={CX - 18} y1={CY + 2} x2={CX - 7} y2={CY + 2} stroke="#f5c518" strokeWidth={2} />
      {/* Right wing */}
      <line x1={CX + 7} y1={CY + 2} x2={CX + 18} y2={CY + 2} stroke="#f5c518" strokeWidth={2} />
      {/* Centre dot */}
      <circle cx={CX} cy={CY + 2} r={2.5} fill="#f5c518" />
      {/* Nose tick */}
      <line x1={CX} y1={CY - 4} x2={CX} y2={CY - 1} stroke="#f5c518" strokeWidth={1.5} />
    </svg>
  )
}

/* ───────── GPS fix label ───────── */
function gpsLabel(fix?: number | null): { text: string; cls: string } {
  if (fix == null) return { text: 'GPS —', cls: 'gps-none' }
  if (fix === 0) return { text: 'No GPS', cls: 'gps-none' }
  if (fix === 1) return { text: 'No Fix', cls: 'gps-none' }
  if (fix === 2) return { text: '2D Fix', cls: 'gps-2d' }
  if (fix === 3) return { text: '3D Fix', cls: 'gps-3d' }
  if (fix === 4) return { text: 'DGPS', cls: 'gps-3d' }
  if (fix >= 5) return { text: 'RTK', cls: 'gps-rtk' }
  return { text: `Fix ${fix}`, cls: 'gps-2d' }
}

/* ───────── Battery bar ───────── */
function BattBar({ pct }: { pct: number | null | undefined }) {
  if (pct == null) return <span className="dc-val">—</span>
  const cls = pct > 50 ? 'bat-ok' : pct > 20 ? 'bat-warn' : 'bat-crit'
  return (
    <span className={`bat-bar ${cls}`}>
      <span className="bat-fill" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      <span className="bat-text">{pct}%</span>
    </span>
  )
}

/* ───────── Main card ───────── */
export default function DroneCard({ drone }: { drone: DroneCardData }) {
  const navigate = useNavigate()
  const gps = gpsLabel(drone.gps_fix_type)

  const hasGpsPos = !!(drone.last_lat && drone.last_lng)
  const altStr = drone.altitude != null ? `${drone.altitude.toFixed(1)}m` : '—'
  const spdStr = drone.speed != null ? `${drone.speed.toFixed(1)}m/s` : '—'
  const hdgStr = drone.heading != null ? `${Math.round(drone.heading)}°` : '—'

  const statusDot = (s: string) => {
    if (s === 'online' || s === 'active' || s === 'flying') return 'dot-green'
    if (s === 'maintenance') return 'dot-yellow'
    return 'dot-grey'
  }

  return (
    <div
      className="dc-card"
      onClick={() => navigate(`/drones/${drone.id}`)}
      title={`${drone.drone_id} — кликни для деталей`}
    >
      {/* ── Header ── */}
      <div className="dc-header">
        <span className="dc-name">{drone.drone_id}</span>
        <span className={`dc-dot ${statusDot(drone.status)}`} title={drone.status} />
      </div>

      {/* ── Tail / model ── */}
      {(drone.tail_number || drone.drone_model) && (
        <div className="dc-sub">
          {drone.tail_number && <span>{drone.tail_number}</span>}
          {drone.drone_model && <span className="dc-model">{drone.drone_model}</span>}
        </div>
      )}

      {/* ── ADI ── */}
      <div className="dc-adi-wrap">
        <ADI roll={drone.roll ?? 0} pitch={drone.pitch ?? 0} />
      </div>

      {/* ── Telemetry rows ── */}
      <div className="dc-telem">
        {/* GPS */}
        <div className="dc-row">
          <span className={`dc-gps-badge ${gps.cls}`}>{gps.text}</span>
          {hasGpsPos && (
            <span className="dc-coords">
              {parseFloat(drone.last_lat!).toFixed(4)}, {parseFloat(drone.last_lng!).toFixed(4)}
            </span>
          )}
        </div>

        {/* Alt + Speed */}
        <div className="dc-row dc-row-split">
          <span className="dc-metric">
            <span className="dc-lbl">ALT</span>
            <span className="dc-val">{altStr}</span>
          </span>
          <span className="dc-sep">|</span>
          <span className="dc-metric">
            <span className="dc-lbl">SPD</span>
            <span className="dc-val">{spdStr}</span>
          </span>
          <span className="dc-sep">|</span>
          <span className="dc-metric">
            <span className="dc-lbl">HDG</span>
            <span className="dc-val">{hdgStr}</span>
          </span>
        </div>

        {/* Battery + Flight mode */}
        <div className="dc-row dc-row-split">
          <span className="dc-metric">
            <span className="dc-lbl">BAT</span>
            <BattBar pct={drone.battery_level} />
          </span>
          {drone.flight_mode && (
            <span className="dc-mode-badge">{drone.flight_mode}</span>
          )}
        </div>
      </div>
    </div>
  )
}
